/**
 * Svelte action : swipe-back depuis le bord gauche, façon iOS interactive
 * pop gesture. À appliquer sur le wrapper du contenu sub-view.
 *
 * Algo :
 *  - touchstart près du bord gauche (≤ EDGE_DETECTION_PX) → on tracke
 *  - touchmove → applique translateX(deltaX) sur l'élément
 *  - touchend :
 *      - si delta > 40% width OU vélocité > 0.5 px/ms → animate off + onBack
 *      - sinon snap-back animé
 *  - geste vertical détecté → abort (ne hijack pas le scroll)
 *
 * Limites v0 : pas de parallax du screen précédent (il n'est pas rendu).
 * Pendant le slide, l'utilisateur voit le `background-color` du body.
 * On ajoutera le parallax en Phase D si besoin.
 *
 * Usage :
 *   <div use:swipeBack={{ onBack: goBack, enabled: inSubView }}>...</div>
 */

import { haptic } from "./haptic";

export interface SwipeBackOptions {
  onBack: () => void;
  /** Si false, le geste est ignoré (action toujours montée mais inactive). */
  enabled?: boolean;
}

const EDGE_DETECTION_PX = 25;
const COMPLETE_THRESHOLD_RATIO = 0.4;
const VELOCITY_THRESHOLD = 0.5; // px/ms
const SLIDE_OUT_MS = 220;
const SNAP_BACK_MS = 200;
// Courbe iOS-like (out-quart approximation). Cf. Apple HIG —
// `cubic-bezier(0.32, 0.72, 0, 1)` est la signature standard utilisée
// dans plusieurs réimplems de l'animation interactive native.
const EASING = "cubic-bezier(0.32, 0.72, 0, 1)";

export function swipeBack(node: HTMLElement, options: SwipeBackOptions) {
  let opts = options;
  let startX = 0;
  let startY = 0;
  let startTime = 0;
  let active = false;
  let aborted = false;
  let currentDelta = 0;

  function setTransform(x: number) {
    node.style.transform = x === 0 ? "" : `translateX(${x}px)`;
  }

  function animateTo(x: number, ms: number, then?: () => void) {
    node.style.transition = `transform ${ms}ms ${EASING}`;
    setTransform(x);
    window.setTimeout(() => {
      node.style.transition = "";
      then?.();
    }, ms);
  }

  function onTouchStart(e: TouchEvent) {
    if (opts.enabled === false) return;
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    if (t.clientX > EDGE_DETECTION_PX) return;
    startX = t.clientX;
    startY = t.clientY;
    startTime = Date.now();
    active = true;
    aborted = false;
    currentDelta = 0;
    node.style.transition = ""; // override toute transition résiduelle
  }

  function onTouchMove(e: TouchEvent) {
    if (!active || aborted) return;
    const t = e.touches[0];
    const dx = t.clientX - startX;
    const dy = Math.abs(t.clientY - startY);

    // Verrou directionnel : si l'utilisateur scroll verticalement avant
    // d'avoir bougé horizontalement de façon significative, on lâche.
    if (Math.abs(dx) < 10 && dy > 10) {
      aborted = true;
      setTransform(0);
      return;
    }
    if (dx <= 0) {
      currentDelta = 0;
      setTransform(0);
      return;
    }
    currentDelta = dx;
    setTransform(dx);
    // preventDefault pour empêcher le scroll horizontal natif
    if (e.cancelable) e.preventDefault();
  }

  function onTouchEnd() {
    if (!active) return;
    active = false;
    if (aborted) {
      animateTo(0, SNAP_BACK_MS);
      return;
    }
    const dt = Date.now() - startTime;
    const velocity = currentDelta / Math.max(dt, 1);
    const passed =
      currentDelta > window.innerWidth * COMPLETE_THRESHOLD_RATIO ||
      velocity > VELOCITY_THRESHOLD;

    if (passed) {
      haptic("light");
      // Animate off-screen puis appelle onBack. Le 1-frame "flash" possible
      // (l'élément est translateX(100vw) avant le re-render) est minimisé en
      // resettant le transform via requestAnimationFrame APRÈS le swap.
      animateTo(window.innerWidth, SLIDE_OUT_MS, () => {
        opts.onBack();
        requestAnimationFrame(() => setTransform(0));
      });
    } else {
      animateTo(0, SNAP_BACK_MS);
    }
  }

  node.addEventListener("touchstart", onTouchStart, { passive: true });
  // touchmove : passive=false pour pouvoir preventDefault sur le scroll
  node.addEventListener("touchmove", onTouchMove, { passive: false });
  node.addEventListener("touchend", onTouchEnd, { passive: true });
  node.addEventListener("touchcancel", onTouchEnd, { passive: true });

  return {
    update(newOpts: SwipeBackOptions) {
      opts = newOpts;
    },
    destroy() {
      node.removeEventListener("touchstart", onTouchStart);
      node.removeEventListener("touchmove", onTouchMove);
      node.removeEventListener("touchend", onTouchEnd);
      node.removeEventListener("touchcancel", onTouchEnd);
    },
  };
}
